import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

// ===== Types =====
export type ToastVariant = "critical" | "error" | "warning" | "info" | "success";

 type ShowToastOptions = {
  variant: ToastVariant;
  message: string;
  title?: string;
  life?: number; // ms; 0 => sticky
  dismissible?: boolean;
};

// ===== Public API (call from anywhere) =====
let _showImpl: ((opts: ShowToastOptions) => string) | null = null;

export function showToast(opts: ShowToastOptions): string {
  return _showImpl ? _showImpl(opts) : "";
}


 interface ToastProviderProps {
  children: React.ReactNode;
  position?: "top-right" | "bottom-right" | "top-left" | "bottom-left";
  max?: number;
}
// ===== Provider =====
 const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = "bottom-right",
  max = 20,}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, number>());

  // Inject minimal CSS once (self-contained; no App.css edits needed)
  useEffect(() => {
    if (document.getElementById("ct-styles")) return;
    const style = document.createElement("style");
    style.id = "ct-styles";
    style.textContent = baseStyles;
    document.head.appendChild(style);
    return () => {
      // leave styles in place across unmounts intentionally
    };
  }, []);

  const remove = (id: string) => {
    const t = timers.current.get(id);
    if (t) window.clearTimeout(t);
    timers.current.delete(id);
    setToasts((prev) => prev.filter((x) => x.id !== id));
  };

  // Wire public API
  useEffect(() => {
    _showImpl = (opts) => {
      const id = Math.random().toString(36).slice(2);
      const item: ToastItem = {
        id,
        createdAt: Date.now(),
        ...defaultToast,
        ...opts,
      };

      setToasts((prev) => {
        const next = [...prev, item];
        // keep last N
        return next.slice(Math.max(0, next.length - max));
      });

      const life = opts.life ?? defaultToast.life!;
      if (life > 0) {
        const timeout = window.setTimeout(() => remove(id), life);
        timers.current.set(id, timeout);
      }
      return id;
    };
    return () => {
      _showImpl = null;
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current.clear();
    };
  }, [max]);

  const posClass = useMemo(() => `ct-container ${position}`, [position]);

  return (
    <>
      {children}
      {createPortal(
        <div className={posClass} aria-live="polite" aria-atomic="false">
          {toasts.map((t) => (
            <ToastCard key={t.id} t={t} onClose={() => remove(t.id)} />
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

// ===== Internal types & defaults =====
interface ToastItem extends Required<ShowToastOptions> {
  id: string;
  createdAt: number;
}

const defaultToast: Required<ShowToastOptions> = {
  variant: "info",
  title: "",
  message: "",
  life: 4000,
  dismissible: true,
};

// ===== Toast Card =====
const ToastCard: React.FC<{ t: ToastItem; onClose: () => void }> = ({ t, onClose }) => {
  const [enter, setEnter] = useState(false);

  useEffect(() => {
    const r = requestAnimationFrame(() => setEnter(true));
    return () => cancelAnimationFrame(r);
  }, []);

  return (
    <div className={`ct-toast ${t.variant} ${enter ? "enter" : ""}`} role="status">
      <div className="ct-body">
        <div className="ct-text">
          {t.title ? <div className="ct-title">{t.title}</div> : null}
          <div className="ct-message">{t.message}</div>
        </div>
        {t.dismissible ? (
          <button className="ct-close" onClick={onClose} aria-label="Dismiss">×</button>
        ) : null}
      </div>
      {t.life > 0 ? <div className="ct-progress" style={{ animationDuration: `${t.life}ms` }} /> : null}
    </div>
  );
};

// ===== Styles (CSS-in-JS injected) =====
const baseStyles = `
.ct-container { position: fixed; z-index: 99999; display: flex; flex-direction: column; gap: 10px; pointer-events: none; }
.ct-container.top-right { top: 16px; right: 16px; }
.ct-container.bottom-right { bottom: 16px; right: 16px; }
.ct-container.top-left { top: 16px; left: 16px; }
.ct-container.bottom-left { bottom: 16px; left: 16px; }

.ct-toast { width: min(300px, 85vw); border-radius: 12px; overflow: hidden; box-shadow: var(--shadow-elevated); transform: translateX(24px); opacity: 0; transition: transform .22s ease, opacity .22s ease; pointer-events: auto; }
.ct-toast.enter { transform: translateX(0); opacity: 1; }

.ct-body { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 10px; padding: 12px 14px; color: inherit; }
.ct-title { font-weight: 700; margin-bottom: 2px; }
.ct-message { opacity: .95; }
.ct-close { margin-left: 6px; border: 0; background: transparent; color: inherit; font-size: 18px; cursor: pointer; opacity: .8; }
.ct-close:hover { opacity: 1; }
.ct-progress { height: 3px; width: 100%; background: var(--bg-toast-progress); transform-origin: left; animation: ct-deplete linear forwards; }
@keyframes ct-deplete { from { transform: scaleX(1); } to { transform: scaleX(0); } }

/* Variants (brand colors) */
.ct-toast.critical { background: var(--severity-critical); color: var(--btn-danger-text); }
.ct-toast.error    { background: var(--status-error); color: var(--btn-danger-text); }
.ct-toast.warning  { background: var(--status-warning); color: var(--text-black); }
.ct-toast.info     { background: var(--status-info); color: var(--btn-danger-text); }
.ct-toast.success  { background: var(--status-success); color: var(--btn-danger-text); }
`;

export default ToastProvider;
